/**
 * KudosWidget
 *
 * Peer-to-peer recognition card. Shows on every dashboard. Three views:
 *   1. Employee of the Week — top recipient this calendar week
 *   2. Recent Kudos Feed — latest 5 kudos given org-wide
 *   3. Give Kudos dialog — pick someone + category + message
 *
 * Categories are hardcoded with emoji + color so the look is consistent
 * across the app (and matches the backend `CATEGORIES` constant).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { toast } from 'sonner';
import {
  Trophy, Award, Sparkles, Heart, Users, Rocket, Search, Loader2, Send, X,
  PartyPopper,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('savenes_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const CATEGORY_ICONS = {
  team_player: Users,
  striving: Trophy,
  patient_first: Heart,
  extra_mile: Rocket,
  other: Sparkles,
};

export default function KudosWidget({ compact = false }) {
  const [leader, setLeader] = useState(null);
  const [leaders, setLeaders] = useState([]);
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGive, setShowGive] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [lb, fd] = await Promise.all([
        axios.get(`${API_URL}/api/kudos/leaderboard?period=week`, { headers: authHeader() }),
        axios.get(`${API_URL}/api/kudos/feed?limit=8`, { headers: authHeader() }),
      ]);
      setLeader(lb.data?.employee_of_period || null);
      setLeaders(lb.data?.leaders || []);
      setFeed(fd.data?.kudos || []);
    } catch {
      // soft-fail; widget collapses gracefully when no kudos yet
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-rose-50 overflow-hidden"
          data-testid="kudos-widget">
      <CardHeader className="pb-3 flex flex-row items-center justify-between gap-2 flex-wrap">
        <CardTitle className="text-sm font-semibold text-[#92400E] flex items-center gap-2">
          <PartyPopper className="w-5 h-5 text-amber-600" />
          Kudos & Employee of the Week
        </CardTitle>
        <Button size="sm" onClick={() => setShowGive(true)}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="give-kudos-btn">
          <Award className="w-3.5 h-3.5 mr-1" /> Give kudos
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="text-center py-6 text-xs text-[#92400E]">
            <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…
          </div>
        ) : (
          <>
            {/* Employee of the Week trophy */}
            {leader ? (
              <div className="bg-white/70 backdrop-blur-sm rounded-lg p-3 border border-amber-200"
                   data-testid="employee-of-week">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shrink-0 shadow-md">
                    <Trophy className="w-7 h-7 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">
                      Employee of the Week
                    </div>
                    <div className="text-base font-bold text-[#0A2342] truncate">
                      {leader.name}
                    </div>
                    <div className="text-xs text-[#64748B] flex items-center gap-2">
                      <span>{leader.role?.replace(/_/g, ' ')}</span>
                      <span>·</span>
                      <span className="font-semibold text-amber-700">{leader.kudos_count} kudos</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-3 text-xs text-[#92400E]/70 italic"
                   data-testid="no-employee-of-week">
                No kudos this week yet — be the first to recognize a teammate! 🎉
              </div>
            )}

            {/* Mini-leaderboard rows 2-5 */}
            {leaders.length > 1 && !compact && (
              <div>
                <div className="text-[10px] uppercase tracking-wide text-[#92400E] mb-1.5">
                  This week's leaders
                </div>
                <div className="space-y-1">
                  {leaders.slice(1, 5).map((l, i) => (
                    <div key={l.user_id}
                         className="flex items-center gap-2 text-xs bg-white/60 rounded px-2 py-1 border border-amber-100"
                         data-testid={`leader-row-${l.user_id}`}>
                      <span className="text-[#92400E] font-semibold w-5">#{i + 2}</span>
                      <span className="text-[#0A2342] font-medium truncate flex-1">{l.name}</span>
                      <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                        {l.kudos_count}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent feed */}
            {feed.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wide text-[#92400E] mb-1.5">
                  Recent kudos
                </div>
                <div className="space-y-1.5">
                  {feed.slice(0, compact ? 3 : 5).map(k => {
                    const Icon = CATEGORY_ICONS[k.category] || Sparkles;
                    return (
                      <div key={k.id}
                           className="bg-white/80 rounded-lg p-2 border border-amber-100 text-xs"
                           data-testid={`kudos-item-${k.id}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs shrink-0"
                               style={{ background: `${k.category_color}20`, color: k.category_color }}>
                            <Icon className="w-3.5 h-3.5" />
                          </div>
                          <div className="font-semibold text-[#0A2342] truncate">{k.to_name}</div>
                          <Badge className="text-[10px] ml-auto shrink-0"
                                 style={{ background: `${k.category_color}15`, color: k.category_color, borderColor: `${k.category_color}40` }}>
                            {k.category_emoji} {k.category_label}
                          </Badge>
                        </div>
                        <div className="text-[#0A2342] italic">"{k.message}"</div>
                        <div className="text-[10px] text-[#64748B] mt-1">— {k.from_name}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>

      <GiveKudosDialog open={showGive} onClose={() => { setShowGive(false); load(); }} />
    </Card>
  );
}

function GiveKudosDialog({ open, onClose }) {
  const [categories, setCategories] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [picked, setPicked] = useState(null);
  const [recipQuery, setRecipQuery] = useState('');
  const [category, setCategory] = useState('team_player');
  const [customLabel, setCustomLabel] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API_URL}/api/kudos/categories`, { headers: authHeader() })
      .then(r => setCategories(r.data?.categories || [])).catch(() => {});
    // Reset on open
    setPicked(null);
    setRecipQuery('');
    setCategory('team_player');
    setCustomLabel('');
    setMessage('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await axios.get(`${API_URL}/api/kudos/recipients/search`,
          { params: { q: recipQuery }, headers: authHeader() });
        setRecipients(r.data?.users || []);
      } finally { setSearching(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [recipQuery, open]);

  const submit = async () => {
    if (!picked) { toast.error('Pick a recipient'); return; }
    if (!message.trim()) { toast.error('Add a short message'); return; }
    if (category === 'other' && !customLabel.trim()) {
      toast.error('Add a custom label for "Other"'); return;
    }
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/kudos`, {
        to_user_id: picked.id,
        category,
        custom_label: category === 'other' ? customLabel : undefined,
        message,
      }, { headers: authHeader() });
      toast.success(`Kudos sent to ${picked.name}! 🎉`);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send kudos');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="give-kudos-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-600" /> Give kudos
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {/* Recipient */}
          <div>
            <Label>Recognize</Label>
            {picked ? (
              <div className="flex items-center gap-2 mt-1 p-2 border border-amber-200 rounded bg-amber-50"
                   data-testid="picked-recipient">
                <div className="w-8 h-8 rounded-full bg-amber-200 flex items-center justify-center text-xs font-bold text-amber-900">
                  {picked.name.split(/\s+/).slice(0, 2).map(x => x[0]).join('').toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#0A2342] truncate">{picked.name}</div>
                  <div className="text-[10px] text-[#64748B]">{picked.role?.replace(/_/g, ' ')}</div>
                </div>
                <button onClick={() => setPicked(null)}
                        className="text-[#64748B] hover:text-[#991B1B]" aria-label="Clear">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <div className="relative mt-1">
                  <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-[#94A3B8]" />
                  <Input className="pl-8" value={recipQuery}
                         onChange={e => setRecipQuery(e.target.value)}
                         placeholder="Search teammate by name…"
                         data-testid="recipient-search-input" />
                </div>
                {recipQuery !== '' && (
                  <div className="border border-[#E2E8F0] rounded mt-1 max-h-40 overflow-y-auto bg-white">
                    {searching ? (
                      <div className="p-2 text-xs text-[#64748B] text-center">Searching…</div>
                    ) : recipients.length === 0 ? (
                      <div className="p-2 text-xs text-[#64748B] text-center">No matches</div>
                    ) : recipients.map(r => (
                      <button key={r.id} onClick={() => setPicked(r)}
                              className="w-full text-left px-2 py-1.5 text-xs hover:bg-amber-50 border-b border-[#F1F5F9] last:border-0 flex items-center justify-between"
                              data-testid={`pick-recipient-${r.id}`}>
                        <span>
                          <div className="font-medium text-[#0A2342]">{r.name}</div>
                          <div className="text-[10px] text-[#64748B]">{r.role?.replace(/_/g, ' ')}</div>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Category */}
          <div>
            <Label>Category</Label>
            <div className="grid grid-cols-2 gap-1.5 mt-1">
              {categories.map(c => (
                <button key={c.code}
                        onClick={() => setCategory(c.code)}
                        className={`text-left p-2 rounded border text-xs transition-colors ${
                          category === c.code
                            ? 'border-amber-400 bg-amber-50 text-[#92400E]'
                            : 'border-[#E2E8F0] hover:bg-[#F8FAFC]'
                        }`}
                        data-testid={`pick-category-${c.code}`}>
                  <span className="mr-1">{c.emoji}</span>
                  <span className="font-medium">{c.label}</span>
                </button>
              ))}
            </div>
            {category === 'other' && (
              <Input value={customLabel} onChange={e => setCustomLabel(e.target.value)}
                     placeholder="e.g. Best snack contribution"
                     className="mt-2" data-testid="custom-label-input" />
            )}
          </div>

          {/* Message */}
          <div>
            <Label>Message</Label>
            <Textarea rows={3} value={message} onChange={e => setMessage(e.target.value)}
                      placeholder="Tell them why they're awesome…"
                      maxLength={400}
                      data-testid="kudos-message-input" />
            <div className="text-[10px] text-[#94A3B8] mt-0.5 text-right">{message.length}/400</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !picked}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="kudos-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
            Send kudos
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
